import type { LlmExtendedFields } from '../../llm/model-utils';
import { apiBool } from '../../llm/model-utils';
import { isAdminBlockedModelStatus } from '../../../../services';

export type ChatFeatureContext = {
  thinkingEnabled: boolean;
  webSearchEnabled: boolean;
  codeInterpreterEnabled: boolean;
  mcpActive: boolean;
};

export type ModelWithCapabilities = {
  name?: string;
  id?: number;
  provider_name?: string;
  proverID?: number;
  provider_id?: number;
  input_price_per_million?: number;
  output_price_per_million?: number;
  input_token_pricing?: number;
  output_token_pricing?: number;
} & Partial<LlmExtendedFields>;

export type CurrentChatModelRef = {
  id?: number | null;
  name?: string | null;
  providerId?: number | null;
};

export type CompatibleModelPick =
  | { ok: true; model: ModelWithCapabilities; switched: boolean }
  | { ok: false };

function capabilityFlag(val: unknown): boolean {
  const parsed = apiBool(val);
  return parsed === true;
}

function openAiThinkingRule(modelName: string): boolean {
  const n = modelName.toLowerCase();
  return n.startsWith('gpt-5') || n.startsWith('o1') || n.startsWith('o3');
}

function supportsReasoning(model: ModelWithCapabilities): boolean {
  const explicit = apiBool(model.supports_reasoning);
  if (explicit !== undefined) {
    return explicit;
  }
  const provider = (model.provider_name || '').toLowerCase();
  return provider === 'openai' && openAiThinkingRule(model.name || '');
}

export function requirementsFromContext(ctx: ChatFeatureContext): {
  requireTools: boolean;
  requireReasoning: boolean;
} {
  const requireTools = ctx.webSearchEnabled || ctx.codeInterpreterEnabled || ctx.mcpActive;
  const requireReasoning = ctx.thinkingEnabled;
  return { requireTools, requireReasoning };
}

export function hasActiveFeatureRequirements(ctx: ChatFeatureContext): boolean {
  const { requireTools, requireReasoning } = requirementsFromContext(ctx);
  return requireTools || requireReasoning;
}

export function incompatibilityReason(
  model: ModelWithCapabilities,
  ctx: ChatFeatureContext,
): string | null {
  const { requireTools, requireReasoning } = requirementsFromContext(ctx);
  if (!requireTools && !requireReasoning) {
    return null;
  }
  const name = model.name || '';
  const provider = (model.provider_name || '').toLowerCase();

  if (requireTools && !capabilityFlag(model.supports_tools)) {
    return 'Does not support tools (MCP, web search, or code interpreter)';
  }
  if (requireReasoning && !supportsReasoning(model)) {
    return 'Does not support reasoning (thinking)';
  }
  if (requireReasoning && provider === 'openai' && !openAiThinkingRule(name)) {
    return 'Thinking requires a GPT-5 or o-series model on OpenAI';
  }
  return null;
}

export function isModelCompatible(model: ModelWithCapabilities, ctx: ChatFeatureContext): boolean {
  return incompatibilityReason(model, ctx) === null;
}

export function filterModelsByCapabilities<T extends ModelWithCapabilities>(
  models: T[],
  ctx: ChatFeatureContext,
): T[] {
  return models.filter((m) => isModelCompatible(m, ctx));
}

export function modelCostScore(model: ModelWithCapabilities): number {
  const inp = model.input_price_per_million ?? model.input_token_pricing;
  const out = model.output_price_per_million ?? model.output_token_pricing;
  try {
    return Number(inp || 0) + Number(out || 0);
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}

export function modelProviderId(model: ModelWithCapabilities): number {
  return Number(model.proverID ?? model.provider_id ?? 0);
}

function matchesCurrentModel(
  model: ModelWithCapabilities,
  current?: CurrentChatModelRef | null,
): boolean {
  if (!current) {
    return false;
  }
  if (current.id != null && model.id != null && Number(model.id) === Number(current.id)) {
    return true;
  }
  if (!current.name || !model.name) {
    return false;
  }
  if (model.name.toLowerCase() !== String(current.name).toLowerCase()) {
    return false;
  }
  if (current.providerId == null) {
    return true;
  }
  return modelProviderId(model) === Number(current.providerId);
}

function sortByCheapestThenId<T extends ModelWithCapabilities>(models: T[]): T[] {
  return [...models].sort((a, b) => {
    const costDiff = modelCostScore(a) - modelCostScore(b);
    if (costDiff !== 0) {
      return costDiff;
    }
    return Number(a.id || 0) - Number(b.id || 0);
  });
}

export function pickCompatibleModel<T extends ModelWithCapabilities>(options: {
  models: T[];
  ctx: ChatFeatureContext;
  current?: CurrentChatModelRef | null;
}): CompatibleModelPick {
  const candidates = options.models.filter(
    (model) => !isAdminBlockedModelStatus(model.model_status),
  );
  const compatible = filterModelsByCapabilities(candidates, options.ctx);
  if (compatible.length === 0) {
    return { ok: false };
  }
  const currentMatch = compatible.find((model) => matchesCurrentModel(model, options.current));
  if (currentMatch) {
    return { ok: true, model: currentMatch, switched: false };
  }
  return { ok: true, model: sortByCheapestThenId(compatible)[0], switched: true };
}

export function anyAssignedModelSupports(
  models: ModelWithCapabilities[],
  requirement: 'tools' | 'reasoning',
): boolean {
  const ctx: ChatFeatureContext =
    requirement === 'tools'
      ? {
          thinkingEnabled: false,
          webSearchEnabled: false,
          codeInterpreterEnabled: false,
          mcpActive: true,
        }
      : {
          thinkingEnabled: true,
          webSearchEnabled: false,
          codeInterpreterEnabled: false,
          mcpActive: false,
        };
  return models
    .filter((model) => !isAdminBlockedModelStatus(model.model_status))
    .some((model) => isModelCompatible(model, ctx));
}

export function assignedFeatureAvailability(models: ModelWithCapabilities[]): {
  thinkingAvailable: boolean;
  toolsAvailable: boolean;
} {
  return {
    thinkingAvailable: anyAssignedModelSupports(models, 'reasoning'),
    toolsAvailable: anyAssignedModelSupports(models, 'tools'),
  };
}

type EnrichedCapabilityRow = {
  id?: number;
  name?: string;
  supports_tools?: boolean;
  supports_reasoning?: boolean;
  input_price_per_million?: number;
  output_price_per_million?: number;
  input_token_pricing?: number;
  output_token_pricing?: number;
  model_status?: string;
};

export function mergeModelCapabilities<T extends { id?: number; name?: string }>(
  base: T[],
  enriched: EnrichedCapabilityRow[],
): Array<T & Partial<LlmExtendedFields> & EnrichedCapabilityRow> {
  const byId = new Map<number, EnrichedCapabilityRow>();
  const byName = new Map<string, EnrichedCapabilityRow>();
  enriched.forEach((row) => {
    if (row.id != null) {
      byId.set(Number(row.id), row);
    }
    if (row.name) {
      byName.set(row.name.toLowerCase(), row);
    }
  });
  return base.map((item) => {
    const hit =
      (item.id != null ? byId.get(Number(item.id)) : undefined) ||
      (item.name ? byName.get(item.name.toLowerCase()) : undefined);
    if (!hit) {
      return { ...item };
    }
    return {
      ...item,
      supports_tools: hit.supports_tools,
      supports_reasoning: hit.supports_reasoning,
      input_price_per_million: hit.input_price_per_million ?? hit.input_token_pricing,
      output_price_per_million: hit.output_price_per_million ?? hit.output_token_pricing,
      model_status: hit.model_status ?? (item as { model_status?: string }).model_status,
    };
  });
}
